import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthHeader } from "../api";
import { awsConfig } from "../aws-config";
import axios from "axios";

interface Preferences {
  // Push
  pushEnabled: boolean;
  pushNewLead: boolean;
  pushBookingUpdate: boolean;
  pushMessage: boolean;
  pushReview: boolean;
  pushPayment: boolean;
  pushPromotional: boolean;
  // Email
  emailEnabled: boolean;
  emailNewLead: boolean;
  emailBookingUpdate: boolean;
  emailWeeklyDigest: boolean;
  emailPromotional: boolean;
  // SMS
  smsEnabled: boolean;
  smsBookingReminder: boolean;
  smsLeadAlert: boolean;
}

const DEFAULT: Preferences = {
  pushEnabled: true,
  pushNewLead: true,
  pushBookingUpdate: true,
  pushMessage: true,
  pushReview: true,
  pushPayment: true,
  pushPromotional: false,
  emailEnabled: true,
  emailNewLead: true,
  emailBookingUpdate: true,
  emailWeeklyDigest: true,
  emailPromotional: false,
  smsEnabled: false,
  smsBookingReminder: false,
  smsLeadAlert: false,
};

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<Preferences>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const headers = await getAuthHeader();
        const res = await axios.get(`${awsConfig.apiUrl}/notification-preferences`, { headers });
        if (res.data.preferences) setPrefs({ ...DEFAULT, ...res.data.preferences });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function update(key: keyof Preferences, value: boolean) {
    setPrefs(prev => ({ ...prev, [key]: value }));
  }

  async function save() {
    setSaving(true);
    try {
      const headers = await getAuthHeader();
      await axios.put(`${awsConfig.apiUrl}/notification-preferences`, prefs, { headers });
      alert("Preferences saved!");
    } catch (e) { console.error(e); alert("Failed to save"); }
    finally { setSaving(false); }
  }

  function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
    return (
      <button onClick={() => !disabled && onChange(!checked)} disabled={disabled}
        className={"relative w-11 h-6 rounded-full transition-colors " + (checked ? "bg-mouna-blue" : "bg-gray-200") + (disabled ? " opacity-40" : "")}>
        <div className={"absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform " + (checked ? "translate-x-5" : "")}></div>
      </button>
    );
  }

  function Section({ title, description, master, masterKey, children }: any) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-card mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h3 className="font-bold text-navy">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
          <Toggle checked={master} onChange={(v) => update(masterKey, v)} />
        </div>
        {master && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
            {children}
          </div>
        )}
      </div>
    );
  }

  function Row({ label, hint, prefKey, disabled }: any) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-sm font-semibold text-navy">{label}</div>
          {hint && <div className="text-xs text-gray-400">{hint}</div>}
        </div>
        <Toggle checked={(prefs as any)[prefKey]} onChange={(v) => update(prefKey, v)} disabled={disabled}/>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-5 pb-4 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigate(-1)} className="text-gray-400 text-sm font-semibold">← Back</button>
          <button onClick={save} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <h1 className="font-display text-2xl font-bold text-navy">Notifications</h1>
        <p className="text-sm text-gray-400 mt-1">Choose what alerts you receive</p>
      </div>

      <div className="px-5 pt-4 pb-24">
        <Section title="📱 Push Notifications" description="Real-time alerts on your device"
          master={prefs.pushEnabled} masterKey="pushEnabled">
          <Row label="New leads" hint="When clients request your services" prefKey="pushNewLead"/>
          <Row label="Booking updates" hint="Status changes, confirmations" prefKey="pushBookingUpdate"/>
          <Row label="Messages" hint="New messages from clients" prefKey="pushMessage"/>
          <Row label="Reviews" hint="New reviews on your profile" prefKey="pushReview"/>
          <Row label="Payments" hint="Payment confirmations and earnings" prefKey="pushPayment"/>
          <Row label="Promotional" hint="Special offers and platform news" prefKey="pushPromotional"/>
        </Section>

        <Section title="📧 Email Notifications" description="Important updates to your inbox"
          master={prefs.emailEnabled} masterKey="emailEnabled">
          <Row label="New leads" hint="When someone wants to hire you" prefKey="emailNewLead"/>
          <Row label="Booking updates" hint="Confirmations and changes" prefKey="emailBookingUpdate"/>
          <Row label="Weekly digest" hint="Summary of your week" prefKey="emailWeeklyDigest"/>
          <Row label="Promotional" hint="Tips, news, and special offers" prefKey="emailPromotional"/>
        </Section>

        <Section title="💬 SMS Notifications" description="Critical alerts via text message"
          master={prefs.smsEnabled} masterKey="smsEnabled">
          <Row label="Booking reminders" hint="Day-of reminders" prefKey="smsBookingReminder"/>
          <Row label="Urgent leads" hint="High-priority job opportunities" prefKey="smsLeadAlert"/>
        </Section>

        <div className="bg-blue-pale rounded-2xl p-4 text-center">
          <div className="text-sm text-gray-600 mb-2">
            🔔 You can enable browser notifications anytime by clicking the bell icon in your browser bar.
          </div>
        </div>
      </div>
    </div>
  );
}
